import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuList,
  Paper,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import { useQuery } from '@tanstack/react-query';
import {
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { SingleOperators } from 'common/src/query-params/operators';
import { IOrganization, IPage } from 'common/src/types/interfaces';

import { getOrganizations } from 'portal/src/api/OrganizationsService';
import useAuth from 'portal/src/auth/useAuth';
import { getNameInitials } from 'portal/src/utils/names';

import OrganizationListItem from './OrganizationListItem';

const OrganizationSwitcher = () => {
  const { getUser, hasClientSystemAdminAccess, switchOrganization } = useAuth();
  const { organizationId, organization } = getUser();
  const hasSysAdminAccess = hasClientSystemAdminAccess();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchedValue, setSearchedValue] = useState('');

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAnchorEl(null);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const { data: organizations } = useQuery({
    queryKey: ['headerOrganizations'],
    queryFn: async () =>
      await getOrganizations({
        filters: [
          {
            field: 'isActive',
            operator: SingleOperators.EQUALS,
            value: true,
          },
        ],
        sort: 'name',
        sortDirection: 'asc',
      }),
    initialData: { data: [] } as IPage<IOrganization>,
  });

  const filteredOrgs = useMemo(() => {
    const search = searchedValue?.toLowerCase() || '';

    return search
      ? organizations.data.filter((org) =>
          org.name.toLowerCase().includes(search),
        )
      : organizations.data;
  }, [searchedValue, organizations]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearchedValue('');
  };

  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' && event.key !== 'Tab') {
      event.stopPropagation();
    }
  };

  return (
    <>
      <Button
        onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget)}
        sx={{ p: 0 }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 35,
            height: 35,
            border: '1px solid',
            bgcolor: 'text.secondary',
            color: 'secondary.contrastText',
            borderColor: 'secondary.contrastText',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          {getNameInitials(organization.name)}
        </Avatar>
      </Button>
      <Popover
        open={!!anchorEl}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Paper>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6">{organization.name}</Typography>
            {hasSysAdminAccess && (
              <Typography variant="body2" color="gray">
                ID: {organizationId}
              </Typography>
            )}
          </Box>
          <Typography sx={{ px: 2, py: 1 }}>Switch Organizations</Typography>
          <TextField
            label="Search Organization"
            size="small"
            color="secondary"
            value={searchedValue}
            onKeyDown={handleSearchKeyDown}
            onChange={({ target }) => setSearchedValue(target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="secondary" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <IconButton
                    disabled={!searchedValue}
                    onClick={() => setSearchedValue('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
              },
            }}
            sx={{ mx: 2, mb: 0.5 }}
          />
          <MenuList sx={{ pt: 0, maxHeight: '45vh', overflow: 'auto' }}>
            {filteredOrgs.length ? (
              filteredOrgs.map((organization, idx) => (
                <OrganizationListItem
                  key={organization.id ?? `temp-key-${idx}`}
                  orgName={organization.name}
                  orgId={organization.id}
                  handleChangeOrg={async () => {
                    if (organizationId !== organization.id) {
                      handleClose();
                      await switchOrganization(organization.id);
                    }
                  }}
                />
              ))
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBlock: '5rem',
                }}
              >
                No data found
              </Box>
            )}
          </MenuList>
        </Paper>
      </Popover>
    </>
  );
};

export default OrganizationSwitcher;
